import React, { useState, useEffect } from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { Question, Serializer, ElementFactory, ItemValue } from "survey-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/sharp-regular-svg-icons";

export class PillMultiSelectButtonQuestionModel extends Question {
  static type = "pillmultiselectbutton";

  private _choices: ItemValue[] = [];

  constructor(name: string) {
    super(name);
  }

  getType() {
    return PillMultiSelectButtonQuestionModel.type;
  }

  get choices(): ItemValue[] {
    return this._choices;
  }

  set choices(val: ItemValue[]) {
    this._choices = val;
  }

  get visibleChoices(): ItemValue[] {
    return this.choices;
  }

  get displayMode(): "pill" | "compact" {
    return this.getPropertyValue("displayMode", "pill");
  }

  set displayMode(val: "pill" | "compact") {
    this.setPropertyValue("displayMode", val);
  }

  public getDefaultValue(): unknown {
    return [];
  }

  public isValueEmpty(value: unknown): boolean {
    return !Array.isArray(value) || value.length === 0;
  }
}

interface PillMultiSelectButtonQuestionComponentProps {
  question: PillMultiSelectButtonQuestionModel;
}

export function PillMultiSelectButtonQuestionComponent({
  question,
}: PillMultiSelectButtonQuestionComponentProps) {
  const currentValue = question.value;
  const valueArray: string[] = Array.isArray(currentValue)
    ? currentValue
    : currentValue
      ? [currentValue]
      : [];

  const [selectedValues, setSelectedValues] = useState<string[]>(valueArray);
  const isReadOnly = question.isReadOnly;

  useEffect(() => {
    const newValueArray: string[] = Array.isArray(question.value)
      ? question.value
      : question.value
        ? [question.value]
        : [];
    setSelectedValues(newValueArray);
  }, [question.value]);

  const handleChange = (value: string) => {
    if (isReadOnly) return;

    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    question.value = newValues;
  };

  const displayMode = question.displayMode || "pill";

  if (displayMode === "compact") {
    return (
      <div
        className={question.cssClasses?.root}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "0",
        }}
      >
        {question.visibleChoices.map((choice: unknown) => {
          const typedChoice = choice as {
            value: string;
            subtitle?: string;
            iconType?: string;
          };
          const isSelected = selectedValues.includes(typedChoice.value);

          return (
            <label
              key={typedChoice.value}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                padding: "2px 0",
                cursor: isReadOnly ? "default" : "pointer",
                opacity: isReadOnly ? 0.7 : 1,
                fontSize: "14px",
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleChange(typedChoice.value)}
                disabled={isReadOnly}
                style={{
                  width: "16px",
                  height: "16px",
                  margin: "2px 0 0 0",
                  cursor: isReadOnly ? "default" : "pointer",
                  accentColor: "#8f8bad",
                }}
              />
              <span>
                {typedChoice.value}
                {typedChoice.subtitle && ` (${typedChoice.subtitle})`}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={question.cssClasses?.root}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "0",
      }}
    >
      {question.visibleChoices.map((choice: unknown) => {
        const typedChoice = choice as {
          value: string;
          subtitle?: string;
          iconType?: string;
        };
        const isSelected = selectedValues.includes(typedChoice.value);
        const icon = typedChoice.iconType === "x" ? faXmark : faCheck;

        return (
          <button
            key={typedChoice.value}
            type="button"
            onClick={() => handleChange(typedChoice.value)}
            disabled={isReadOnly}
            style={{
              width: "100%",
              height: "80px",
              backgroundColor: isSelected ? "#252423" : "#fff",
              color: isSelected ? "#fff" : "#252423",
              border: "1px solid",
              borderColor: isSelected ? "#252423" : "rgba(204, 197, 194, 0.5)",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "12px",
              borderRadius: "9999px",
              cursor: isReadOnly ? "default" : "pointer",
              fontWeight: "500",
              textAlign: "left",
              transition: "all 0.2s",
              opacity: isReadOnly ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSelected && !isReadOnly) {
                e.currentTarget.style.backgroundColor = "#252423";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "#252423";
                const iconContainer = e.currentTarget.querySelector(
                  ".icon-container",
                ) as HTMLElement;
                if (iconContainer) {
                  iconContainer.style.backgroundColor = "#efff92";
                }
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected && !isReadOnly) {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = "#252423";
                e.currentTarget.style.borderColor = "rgba(204, 197, 194, 0.5)";
                const iconContainer = e.currentTarget.querySelector(
                  ".icon-container",
                ) as HTMLElement;
                if (iconContainer) {
                  iconContainer.style.backgroundColor = "#cfd7df";
                }
              }
            }}
          >
            <div
              className="icon-container"
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "28px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: isSelected ? "#efff92" : "#cfd7df",
                flexShrink: 0,
                transition: "background-color 0.2s",
              }}
            >
              <FontAwesomeIcon
                icon={icon}
                style={{
                  color: "#252423",
                  width: "20px",
                  height: "20px",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div>{typedChoice.value}</div>
              {typedChoice.subtitle && (
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "2px",
                    opacity: 0.8,
                  }}
                >
                  {typedChoice.subtitle}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function registerPillMultiSelectButtonQuestion() {
  Serializer.addProperty("itemvalue", {
    name: "subtitle",
    category: "general",
    displayName: "Subtext",
    type: "string",
  });

  Serializer.addProperty("itemvalue", {
    name: "iconType",
    category: "general",
    displayName: "Icon",
    type: "dropdown",
    choices: ["check", "x"],
  });

  ElementFactory.Instance.registerElement(
    "pillmultiselectbutton",
    (name: string) => new PillMultiSelectButtonQuestionModel(name),
  );

  Serializer.addClass(
    "pillmultiselectbutton",
    [
      {
        name: "choices:itemvalue[]",
        category: "general",
        displayName: "Choices",
      },
      {
        name: "displayMode",
        category: "general",
        default: "pill",
      },
    ],
    () => new PillMultiSelectButtonQuestionModel(""),
    "question",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    PillMultiSelectButtonQuestionModel.type,
    (propsOrName: string | PillMultiSelectButtonQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new PillMultiSelectButtonQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(PillMultiSelectButtonQuestionComponent, props);
    },
  );
}

export function createPillMultiSelectButtonQuestion(config: {
  name: string;
  title?: string;
  choices: Array<{
    value: string;
    subtitle?: string;
    icon?: "check" | "x";
  }>;
  isRequired?: boolean;
  displayMode?: "pill" | "compact";
}) {
  return {
    type: "pillmultiselectbutton",
    name: config.name,
    title: config.title,
    choices: config.choices,
    isRequired: config.isRequired || false,
    displayMode: config.displayMode || "pill",
  };
}
